import { useCallback, useEffect, useRef } from 'react';
import { refreshAccessToken } from '../apiClient';
import { ensureFreshToken, isKeycloakMode } from '../auth/keycloakClient';

const hasWindow = typeof window !== 'undefined';
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'];
const ACTIVITY_DEBOUNCE_MS = 1000;
const REFRESH_COOLDOWN_MS = 60 * 1000;
const DEFAULT_BUFFER_MS = 5 * 60 * 1000;

export function useTokenRefreshOnActivity({ enabled = true, bufferMs = DEFAULT_BUFFER_MS } = {}) {
    const debounceRef = useRef(null);
    const lastAttemptRef = useRef(0);

    const scheduleRefresh = useCallback(() => {
        if (!hasWindow || !enabled) {
            return;
        }

        if (debounceRef.current) {
            return;
        }

        debounceRef.current = window.setTimeout(async () => {
            debounceRef.current = null;
            const now = Date.now();
            if (now - lastAttemptRef.current < REFRESH_COOLDOWN_MS) {
                return;
            }
            lastAttemptRef.current = now;
            try {
                if (isKeycloakMode()) {
                    const minValiditySeconds = Math.max(0, Math.ceil(bufferMs / 1000));
                    await ensureFreshToken(minValiditySeconds);
                } else {
                    await refreshAccessToken({ bufferMs });
                }
            } catch {}
        }, ACTIVITY_DEBOUNCE_MS);
    }, [bufferMs, enabled]);

    useEffect(() => {
        if (!hasWindow || !enabled) {
            return undefined;
        }

        const handleActivity = () => {
            scheduleRefresh();
        };

        ACTIVITY_EVENTS.forEach((eventName) => {
            window.addEventListener(eventName, handleActivity, { passive: true });
        });

        const handleFocus = () => {
            scheduleRefresh();
        };
        window.addEventListener('focus', handleFocus);

        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                scheduleRefresh();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            ACTIVITY_EVENTS.forEach((eventName) => {
                window.removeEventListener(eventName, handleActivity);
            });
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibility);
            if (debounceRef.current) {
                window.clearTimeout(debounceRef.current);
                debounceRef.current = null;
            }
        };
    }, [enabled, scheduleRefresh]);
}

export default useTokenRefreshOnActivity;
